import { useTheme } from '@mui/material';
import { useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import CardActions from '@mui/material/CardActions';
import Grid from '@mui/material/Grid2';
import Collapse from '@mui/material/Collapse';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import LoadingButton from '@mui/lab/LoadingButton';
import { TCustomTheme } from '@mgm/common/theme';
import { PRICING_OPTIONS } from '../data';
import { useSetPricingPlanAPI } from '../hooks/api/useOnboarding';
import { useQueryParams } from '../hooks/useCommon';
import { useAppSelector } from '../hooks/useAppStore';
import { getTokenDetails } from '../store/authenticate/selectors';
import { LanguagePopover } from './base/BPopover';

const Pricing = () => {
  const theme = useTheme() as TCustomTheme;
  const { t } = useTranslation('pricing');
  const [expandedIndex, setExpandedIndex] = useState<{ planIndex: number; featureIndex: number } | null>(null); // Tracks expanded feature

  const { mutateAsync: setPlanMutate, isLoading } = useSetPricingPlanAPI();
  const query = useQueryParams();
  const tokenDetails = useAppSelector(getTokenDetails);

  const handleFeatureExpandClick = (planIndex: number, featureIndex: number) => {
    setExpandedIndex((prev) => (prev?.planIndex === planIndex && prev?.featureIndex === featureIndex
      ? null // Collapse if already expanded
      : { planIndex, featureIndex }), // Expand the clicked feature
      // eslint-disable-next-line function-paren-newline
    );
  };

  const selectPlan = async (val: 'free' | 'lite' | 'standard' | 'enterprise') => {
    const data = {
      planName: val,
      shopName: query.get('shopName') as string,
      planChange: query.get('channel') === 'app' ? true : null,
    };
    await setPlanMutate(data);
  };

  return (
    <Box py={6}>
      <Box
        sx={{
          position: 'relative',
          backgroundColor: theme.palette.alternate.main,
          backgroundImage: `linear-gradient(120deg, ${theme.palette.background.paper} 0%, ${theme.palette.alternate.main} 100%)`,
          marginTop: -13,
          paddingTop: 13,
        }}
      >
        <Box>
          <Box marginBottom={4}>
            <Typography
              variant="h2"
              gutterBottom
              align="center"
              sx={{
                fontWeight: 900,
              }}
            >
              {t('superchargeYourShopify')}
            </Typography>
            <Typography
              variant="h2"
              gutterBottom
              color="primary"
              align="center"
              sx={{
                fontWeight: 900,
              }}
            >
              ManageMate
            </Typography>
            <Typography
              pt={4}
              pb={2}
              variant="h6"
              component="p"
              color="text.primary"
              align="center"
            >
              {t('atManageMateWeUnderstand')}
              <br />
              {t('whichIsWhyWe')}
              <br />
              {t('whetherYoureSmall')}
              <br />
              {t('manageMateHasAPlan')}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <LanguagePopover />
            </Box>
          </Box>
        </Box>
      </Box>
      <Grid container spacing={4}>
        {PRICING_OPTIONS.map((item, planIndex) => (
          <Grid
            size={{
              xs: 12,
              md: 4,
              lg: 3,
            }}
            key={planIndex?.toString()}
          >
            <Box
              component={Card}
              height={1}
              display="flex"
              flexDirection="column"
              variant="outlined"
            >
              <CardContent
                sx={{
                  padding: 4,
                }}
              >
                <Box marginBottom={2}>
                  <Typography variant="h4" fontWeight={600} gutterBottom>
                    {t(`${item.title}`)}
                  </Typography>
                  <Typography color="text.secondary">{t(`${item.subtitle}`)}</Typography>
                </Box>
                <Box display="flex" alignItems="baseline" marginBottom={2}>
                  <Typography variant="h3" fontWeight={700}>
                    {item?.price && item.price}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    fontWeight={700}
                  >
                    {item?.price && t('monthPerUser')}
                  </Typography>
                </Box>
                <Grid container spacing={1}>
                  {item.features.map((feature, featureIndex) => (
                    <Grid size={{ xs: 12 }} key={featureIndex?.toString()}>
                      <Box
                        component={ListItem}
                        disableGutters
                        width="auto"
                        padding={0}
                      >
                        <Box
                          component={ListItemAvatar}
                          minWidth="auto !important"
                          marginRight={2}
                        >
                          <Box
                            component={Avatar}
                            bgcolor={theme.palette.primary.main}
                            sx={{
                              color: 'white',
                            }}
                            width={20}
                            height={20}
                          >
                            <svg
                              width={12}
                              height={12}
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </Box>
                        </Box>
                        <ListItemText primary={t(`${feature.item}`)} />
                        {feature.children?.length && (
                          <IconButton
                            color="primary"
                            onClick={() => handleFeatureExpandClick(planIndex, featureIndex)}
                            aria-expanded={
                              expandedIndex?.planIndex === planIndex
                              && expandedIndex?.featureIndex === featureIndex
                            }
                            aria-label="show more"
                          >
                            <ExpandMoreIcon />
                          </IconButton>
                        )}
                      </Box>
                      {feature.children?.length && (
                        <Collapse
                          in={
                            expandedIndex?.planIndex === planIndex
                            && expandedIndex?.featureIndex === featureIndex
                          }
                          timeout="auto"
                          unmountOnExit
                        >
                          <Stack spacing={1} paddingLeft={4}>
                            {feature.children.map((text) => (
                              <li key={text}>
                                <span
                                  style={{
                                    color: '#677788',
                                    fontSize: 14,
                                  }}
                                >
                                  {t(`${text}`)}
                                </span>
                              </li>
                            ))}
                          </Stack>
                        </Collapse>
                      )}
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
              <Box flexGrow={1} />
              <CardActions sx={{ justifyContent: 'flex-end', padding: 4 }}>
                <LoadingButton
                  loading={isLoading}
                  disabled={
                    tokenDetails.subscription?.subscriptionPlanName === item.value
                    || (item.value === tokenDetails.subscription?.subscriptionPlanName && query.get('channel') === 'app')
                    || isLoading
                  }
                  aria-label={`mgm pricing ${item.value}`}
                  onClick={() => selectPlan(item.value)}
                  size="large"
                  variant="contained"
                >
                  {t('selectedPlan')}
                </LoadingButton>
              </CardActions>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export { Pricing };
